import { Injectable, Injector, OnDestroy } from "@angular/core";
import { Store } from "@ngxs/store";
import { CloudData } from "angular-tag-cloud-module";
import { BehaviorSubject, EMPTY, from, of, Subscription } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";
import {
    EncuestaDTO,
    EnvioDTO,
    InvestigacionDTO,
    ItemPreguntaDTO,
    MarcadorDTO,
    PaisDTO,
    PreguntaDTO,
    RespuestaEncuesta,
    RespuestaUsuario
} from "@DTOS";
import { MarcadorSeleccionado, ProcesoRespuestas, RespuestasHelper } from "@shared/clases";
import {
    GeneroEnum,
    obtenerIdRangoEdad,
    obtenerIdsRangoEdad,
    obtenerNombreGenero,
    obtenerNombreRangoEdad,
    obtenerNombreTipoMarcador,
    OrdenamientosRespuestasEnum,
    PreguntasTipoEnum,
    RangosEdadEnum,
    TipoMarcadorEnum
} from "@shared/enums";
import { PreguntaYRespuestas } from "@shared/serviciosCSV";
import { ExporterRawDataExcel } from "@shared/serviciosExcel";
import { InvestigacionesAcciones, State } from "@shared/states";
import { CuestionariosUtils, Nilable } from "@shared/utils";
import { CambiosGraficosService } from "./cambios-graficos.service";
import { FiltroRespuesta } from "./Filtro.respuestas";
import { Respuesta } from "./Respuesta";
import { FiltrosService } from "./filtros.service";

@Injectable({
    providedIn: "root"
})
export class RespuestasService implements OnDestroy {
    public investigacion: InvestigacionDTO;
    public respuestas: BehaviorSubject<Nilable<Respuesta[] | CloudData[]>> = new BehaviorSubject(null);

    public preguntas: PreguntaDTO[] = [];

    private respuestasEnCrudo: RespuestaUsuario[] = [];
    private paisesDTO: PaisDTO[] = [];

    private filtrosSubscription = new Subscription();
    private preguntaSeleccionadaSubscription: Subscription;

    constructor(private cambiosGraficosService: CambiosGraficosService, private injector: Injector,
        private store: Store, private filtrosService: FiltrosService, private respuestasHelper: RespuestasHelper,
        private cuestionariosUtils: CuestionariosUtils) {}

    ngOnDestroy() {
        this.filtrosSubscription.unsubscribe();
        this.preguntaSeleccionadaSubscription?.unsubscribe();
    }

    /**
     * Cargar preguntas y datos sobre la encuesta pasada por parametro
     *
     * @param encuestaDTO Encuesta de la cual cargar los datos
     */
    public async cargarDatosEncuesta(encuestaDTO: EncuestaDTO) {
        this.paisesDTO = this.store.selectSnapshot((state: State) => state.paises.paises);
        this.cambiosGraficosService.encuestaSeleccionada.next(encuestaDTO);

        const envioDTO = this.investigacion.envios.find(envio =>
            envio.encuestaId === this.cambiosGraficosService.encuestaSeleccionada.value.id) ?? new EnvioDTO();
        this.store.dispatch(new InvestigacionesAcciones.ObtenerRespuestasDia(this.investigacion, envioDTO));
        this.store.dispatch(new InvestigacionesAcciones.ObtenerRespuestas(this.investigacion, encuestaDTO));
        this.store.select((state: State) => state.investigaciones.respuestasEncuestaInvestigacionSeleccionada)
            .pipe(
                filter(respuestasParciales => respuestasParciales != null && respuestasParciales.has(encuestaDTO.id)),
                take(1)
            ).subscribe((respuestasParciales: Map<number, RespuestaEncuesta[]>) => {
                this.cargarRespuestaEncuesta(respuestasParciales.get(encuestaDTO.id) ?? []);
                this.store.select(
                    (state: State) => state.investigaciones.respuestasEncuestaInvestigacionSeleccionada)
                    .pipe(
                        filter(respuestasTotales => respuestasTotales != null && respuestasTotales.has(encuestaDTO.id)
                            && respuestasTotales !== respuestasParciales),
                        take(1)
                    ).subscribe((respuestasTotales: Map<number, RespuestaEncuesta[]>) =>
                        this.cargarRespuestaEncuestaCompletas(respuestasTotales.get(encuestaDTO.id) ?? [])
                    );
            });
    }

    public async obtenerDatosAsync(): Promise<PreguntaYRespuestas[]> {
        const preguntasYRespuestas: PreguntaYRespuestas[] = [];

        await Promise.all(this.preguntas.map(async pregunta => {
            preguntasYRespuestas.push({
                pregunta,
                respuestas: await this.obtenerRespuestasAsync(pregunta)
            });
        }));

        return preguntasYRespuestas;
    }

    public exportarRawData(): void {
        const encuesta = this.cambiosGraficosService.encuestaSeleccionada.value;
        const respuestas = this.injector.get(ProcesoRespuestas).obtenerRespuestasUsuarioMostradas();
        ExporterRawDataExcel.obtener(encuesta, this.cuestionariosUtils).exportar(respuestas);
    }

    public obtenerCantidadDesdeFiltros(filtros: FiltroRespuesta[]): number {
        return this.respuestasHelper.obtenerIndices(this.respuestasEnCrudo, filtros).length;
    }

    public obtenerFiltrosDesdeMarcadores(marcadores: MarcadorDTO[]): FiltroRespuesta[] {
        const filtros: FiltroRespuesta[] = [];

        marcadores.map(marcador => {
            let pregunta: PreguntaDTO;
            let item = new ItemPreguntaDTO();

            if (marcador.valor != null) {
                pregunta = new PreguntaDTO({ text: obtenerNombreTipoMarcador(marcador.tipo) });

                if (marcador.tipo === TipoMarcadorEnum.GENDER) {
                    item = new ItemPreguntaDTO({ text: obtenerNombreGenero(GeneroEnum[marcador.valor]) });
                }

                if (marcador.tipo === TipoMarcadorEnum.COUNTRY) {
                    item = new ItemPreguntaDTO({ text: this.paisesDTO.find(paisDTO =>
                        paisDTO.codigo === marcador.valor)?.nombre ?? "" });
                }

                if (marcador.tipo === TipoMarcadorEnum.AGE_RANGE) {
                    const rangoEdad: [RangosEdadEnum, string] = obtenerIdsRangoEdad()
                        .find(([, nombre]) => nombre === marcador.valor)
                        ?? [RangosEdadEnum.DE_18_A_24, obtenerIdRangoEdad(RangosEdadEnum.DE_18_A_24)];
                    item = new ItemPreguntaDTO({ text: obtenerNombreRangoEdad(rangoEdad[0]) });
                }
            } else {
                pregunta = this.preguntas.find(preguntaDTO =>
                    preguntaDTO.id === marcador.preguntaId) ?? new PreguntaDTO();
                item = pregunta.items.find(itemDTO => itemDTO.id === marcador.itemId) ?? new ItemPreguntaDTO();
            }

            filtros.push({
                esComparativa: marcador.esComparativa,
                pregunta,
                item
            });
        });

        return filtros;
    }

    public iniciar() {
        this.cargarSubscripciones();
    }

    public limpiar() {
        this.filtrosSubscription.unsubscribe();
        this.preguntaSeleccionadaSubscription.unsubscribe();

        this.filtrosSubscription = new Subscription();
        this.preguntaSeleccionadaSubscription = new Subscription();

        this.filtrosService.limpiarFiltros();
        this.respuestas.next([]);
    }

    public agregarMarcador(marcador: MarcadorDTO) {
        const filtros = this.filtrosService.obtenerFiltros();
        this.filtrosService.limpiarFiltros();
        const marcadorSeleccionado: MarcadorSeleccionado = {
            nombre: marcador.nombre,
            activado: true,
            filtros
        };
        this.cambiosGraficosService.agregarMarcadorSeleccionado(marcadorSeleccionado);
    }

    private async cargarRespuestaEncuesta(respuestasEncuesta: RespuestaEncuesta[]) {
        const procesoRespuestas = this.injector.get(ProcesoRespuestas);
        procesoRespuestas.iniciar(respuestasEncuesta);
    }

    private async cargarRespuestaEncuestaCompletas(respuestasEncuesta: RespuestaEncuesta[]) {
        const procesoRespuestas = this.injector.get(ProcesoRespuestas);
        procesoRespuestas.iniciarTotales(respuestasEncuesta);
    }

    private cargarSubscripciones() {
        this.filtrosSubscription.add(
            this.filtrosService.filtros$.subscribe(() => {
                const procesoRespuesta = this.injector.get(ProcesoRespuestas);
                procesoRespuesta.filtrar();
                this.cambiosGraficosService.preguntaSeleccionada.next(
                    this.cambiosGraficosService.preguntaSeleccionada.value
                );
            })
        );

        this.preguntaSeleccionadaSubscription = this.cambiosGraficosService.preguntaSeleccionada
            .pipe(
                switchMap(preguntaSeleccionada => {
                    if (preguntaSeleccionada.texto) {
                        return of(preguntaSeleccionada);
                    }

                    return EMPTY;
                }),
                switchMap(preguntaSeleccionada => from(this.obtenerRespuestasAsync(preguntaSeleccionada)))
            )
            .subscribe(respuestas => {
                this.respuestas.next(respuestas);
                this.cambiosGraficosService.ordernarDatos(OrdenamientosRespuestasEnum.NOMBRE);
            });
    }

    /**
     * Envia las respuestas de la pregunta pasada por parametro
     *
     * @param preguntaSeleccionada Pregunta de las cual enviar las respuestas
     */
    private async obtenerRespuestasAsync(preguntaSeleccionada: PreguntaDTO): Promise<Respuesta[] | CloudData[]> {
        const procesoRespuestas = this.injector.get(ProcesoRespuestas);
        const respuestas = await procesoRespuestas.obtenerRespuestas(preguntaSeleccionada);
        this.reacomodoGraficas(preguntaSeleccionada);
        return respuestas.slice(0);
    }

    private reacomodoGraficas(pregunta: PreguntaDTO) {
        if (pregunta.tipo === PreguntasTipoEnum.TEXT || pregunta.texto === "Por día") {
            this.cambiosGraficosService.mostrarReferencia.next(false);
            return;
        }

        this.cambiosGraficosService.mostrarReferencia.next(true);
    }
}
